import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal, { Field } from './Modal';
import ErrorState from './ErrorState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import {
  uploadFile, downloadFile, fileIcon, uploadErrorMessage, checkUploadable, formatBytes, ACCEPTED_FILE_TYPES, MAX_UPLOAD_BYTES,
} from '../utils/fileUpload';
import { formatDate, todayLocalISO, plural } from '../utils/format';

/**
 * <FileAttachments /> — attach + list files against a single entity
 * (a job / property / form / staff member). Uploads carry entity_type + entity_id
 * so they show up here and in the main Files screen.
 *
 * Props:
 *   entityType ('job'|'property'|'form'|'staff'), entityId
 *   title?           card heading
 *   documentGroup?   default group for quick (instant) uploads
 *   withMeta?        when true, "Attach file" opens a dialog with title / group /
 *                    go-live / expiry (same as the Files page) — e.g. certifications
 *   groups?          group options shown in the dialog
 *   clientToggle?    show a "visible to client" switch per file
 *   onlyGroups?      only list files in these groups
 *   emptyHint?       text under the empty drop zone
 *   readOnly?        list + download only — no attach, remove or visibility toggle (e.g. a read-only job)
 */
const DEFAULT_GROUPS = ['Certificates', 'Training', 'Insurance', 'RAMS', 'O&M Manuals', 'General'];
const EMPTY_FORM = (groups) => ({ file: null, title: '', document_group: groups[0], go_live: '', expire: '' });

const FileAttachments = ({ entityType, entityId, title = 'Attachments', documentGroup = 'Job files', withMeta = false, groups = DEFAULT_GROUPS, clientToggle = false, onlyGroups = null, emptyHint = null, readOnly = false }) => {
  const { confirm } = useConfirm();
  const [files, setFiles] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  // Billing shows the same store filtered to the groups it cares about — one
  // upload area, several views of it.
  const shown = onlyGroups && onlyGroups.length ? files.filter((f) => onlyGroups.includes(f.document_group)) : files;
  const [uploading, setUploading] = useState(null);     // { name, progress } while a quick upload runs
  const [dragOver, setDragOver] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => EMPTY_FORM(groups));
  const [formErr, setFormErr] = useState({});
  const [formProgress, setFormProgress] = useState(null);
  const inputRef = useRef(null);
  const formInputRef = useRef(null);
  const today = todayLocalISO();

  const load = useCallback(async () => {
    if (entityId == null) return;
    try {
      const d = await apiFetch(`/api/contractor/files?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`);
      setFiles(Array.isArray(d?.files) ? d.files : []);
      setLoadErr(null);
    } catch (e) { setLoadErr(e); }
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  // --- Quick (instant) upload — used by the file picker AND drag & drop ---
  const uploadFiles = async (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (!arr.length) return;
    if (entityId == null) { toast.info('Save this record first, then attach files'); return; }
    let ok = 0;
    for (const file of arr) {
      const pre = checkUploadable(file);
      if (pre) { toast.error(pre); continue; }
      setUploading({ name: file.name, progress: 0 });
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await uploadFile(file, { title: file.name, document_group: documentGroup, entity_type: entityType, entity_id: String(entityId) },
          { onProgress: (p) => setUploading({ name: file.name, progress: p }) });
        setFiles((prev) => [{ id: res.id, title: file.name, file_name: res.file_name, file_size: res.file_size, mime_type: res.mime_type, document_group: documentGroup, created_at: new Date().toISOString() }, ...prev]);
        ok += 1;
      } catch (e) { toast.error(uploadErrorMessage(e, file.name)); }
    }
    setUploading(null);
    if (ok) toast.success(ok > 1 ? `${ok} files attached` : 'File attached');
  };
  const onPick = (e) => { const picked = Array.from(e.target.files || []); e.target.value = ''; uploadFiles(picked); };
  const pickForForm = (file) => {
    if (!file) return;
    setForm((s) => ({ ...s, file, title: s.title || file.name.replace(/\.[^.]+$/, '') }));
    setFormErr((s) => ({ ...s, file: checkUploadable(file) || undefined }));
  };
  // Drag & drop emails/PDFs/photos straight onto the card (attaches to this entity).
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (readOnly) return;
    if (!withMeta) { uploadFiles(e.dataTransfer.files); return; }
    const first = e.dataTransfer.files?.[0];
    if (first) { setForm(EMPTY_FORM(groups)); setFormErr({}); pickForForm(first); setShowForm(true); }
  };
  const onDragOver = (e) => { e.preventDefault(); if (!readOnly && !dragOver) setDragOver(true); };
  const onDragLeave = (e) => { if (e.currentTarget === e.target) setDragOver(false); };

  // --- Detailed upload (withMeta) — title / group / dates, like the Files page ---
  const openForm = () => { setForm(EMPTY_FORM(groups)); setFormErr({}); setFormProgress(null); setShowForm(true); };
  const submitForm = async () => {
    const e = {};
    if (!form.file) e.file = 'Choose a file to attach';
    else { const m = checkUploadable(form.file); if (m) e.file = m; }
    if (!form.title.trim()) e.title = 'Give the document a title';
    if (form.go_live && form.expire && form.expire < form.go_live) e.expire = 'Expiry can’t be before the issue date';
    setFormErr(e);
    if (Object.keys(e).length) return;
    setFormProgress(0);
    try {
      const res = await uploadFile(form.file, {
        title: form.title.trim(), document_group: form.document_group, go_live: form.go_live, expire: form.expire,
        entity_type: entityType, entity_id: String(entityId),
      }, { onProgress: setFormProgress });
      setFiles((prev) => [{ id: res.id, title: form.title.trim(), file_name: res.file_name, file_size: res.file_size, mime_type: res.mime_type,
        document_group: form.document_group, go_live: form.go_live || null, expire: form.expire || null, created_at: new Date().toISOString() }, ...prev]);
      toast.success('File attached');
      setShowForm(false);
    } catch (er) { setFormErr({ file: uploadErrorMessage(er, form.file.name) }); }
    finally { setFormProgress(null); }
  };

  const doDownload = async (f) => {
    try { await downloadFile(f.id, f.file_name || f.title); }
    catch (e) { toast.error(e?.status === 404 ? 'That file is no longer available' : apiErrorMessage(e, 'Couldn’t download this file')); }
  };

  // Mark a file client-visible (shows in Client Portal links) or hide it.
  const toggleClientVisible = async (f) => {
    const next = f.client_visible ? 0 : 1;
    setFiles((cur) => cur.map((x) => (x.id === f.id ? { ...x, client_visible: next } : x)));
    try { await apiFetch(`/api/contractor/files/${f.id}`, { method: 'PATCH', body: JSON.stringify({ client_visible: !!next }) }); toast.success(next ? 'Now visible to the client' : 'Hidden from the client'); }
    catch (e) { setFiles((cur) => cur.map((x) => (x.id === f.id ? { ...x, client_visible: f.client_visible } : x))); toast.error(apiErrorMessage(e, 'Couldn’t change who can see it')); }
  };

  const doRemove = async (f) => {
    const name = f.title || f.file_name;
    const ok = await confirm({ title: `Remove “${name}”?`, body: 'It’s taken off this record and out of the Files list, and hidden from clients.', confirmLabel: 'Remove file', tone: 'danger' });
    if (!ok) return;
    const prev = files;
    setFiles((cur) => cur.filter((x) => x.id !== f.id));
    try { await apiFetch(`/api/contractor/files/${f.id}`, { method: 'DELETE' }); toast.success('File removed'); }
    catch (e) { setFiles(prev); toast.error(apiErrorMessage(e, 'Couldn’t remove this file')); }
  };

  const busy = !!uploading;
  return (
    <section className="cw-card" aria-label={title} style={{ overflow: 'hidden', position: 'relative', border: `${dragOver ? 2 : 1.5}px dashed ${dragOver ? 'var(--cw-blue)' : 'var(--cw-border)'}` }}
      onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <div className="cw-card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h3>{title} {shown.length > 0 && <span className="cw-chip" style={{ marginLeft: 6 }}>{shown.length}<span className="cw-sr-only"> {shown.length === 1 ? 'file' : 'files'}</span></span>}</h3>
        {!readOnly && (
          <button type="button" className="cw-btn cw-btn-primary" onClick={() => (withMeta ? openForm() : inputRef.current?.click())} disabled={busy || entityId == null}>
            <i className={`fa-solid ${busy ? 'fa-spinner fa-spin' : 'fa-paperclip'}`} aria-hidden="true" /> {busy ? `Uploading ${uploading.progress}%` : 'Attach file'}
          </button>
        )}
        <input ref={inputRef} type="file" multiple accept={ACCEPTED_FILE_TYPES} tabIndex={-1} aria-hidden="true" style={{ display: 'none' }} onChange={onPick} />
      </div>
      {busy && (
        <div role="progressbar" aria-label={`Uploading ${uploading.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploading.progress} style={{ height: 4, background: 'var(--cw-border-light)' }}>
          <div style={{ width: `${uploading.progress}%`, height: '100%', background: 'var(--cw-blue)' }} />
        </div>
      )}
      {loadErr ? <div style={{ padding: 12 }}><ErrorState compact thing="the attached files" error={loadErr} onRetry={load} /></div>
        : shown.length === 0 ? (
          <div aria-hidden={dragOver ? undefined : true} style={{ margin: 14, border: `${dragOver ? 2 : 1.5}px dashed ${dragOver ? 'var(--cw-blue)' : 'var(--cw-border)'}`, borderRadius: 10, padding: '22px 16px', textAlign: 'center', fontSize: 'var(--cs-text-sm, 0.8125rem)', color: 'var(--cw-text-muted)', background: 'var(--cw-blue-tint, #EBF4FF)' }}>
            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '1.6rem', color: 'var(--cw-blue)', display: 'block', marginBottom: 8 }} />
            <div style={{ fontWeight: 700, color: 'var(--cs-blue-dark, #1565C0)' }}>{dragOver ? `Drop to attach to this ${entityType}` : 'No files attached yet'}</div>
            {!readOnly && <div style={{ marginTop: 3 }}>{emptyHint || <>Drag and drop here, or choose <strong>Attach file</strong> — certificates, photos, RAMS and reports stay linked to this record. Up to {formatBytes(MAX_UPLOAD_BYTES)} each.</>}</div>}
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {shown.map((f, i) => {
              const ic = fileIcon(f.mime_type || f.file_name);
              const exp = f.expire ? String(f.expire).slice(0, 10) : '';
              const expired = exp && exp < today;
              const name = f.title || f.file_name;
              return (
                <li key={f.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', borderTop: '1px solid var(--cw-border-light)', flexWrap: 'wrap' }}>
                  <i className={ic.icon} aria-hidden="true" style={{ color: ic.color, fontSize: '1.1rem' }} />
                  <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                    <div className="cw-meta">
                      {[f.file_size ? formatBytes(f.file_size) : '', f.document_group, f.created_at ? formatDate(f.created_at) : ''].filter(Boolean).join(' · ')}
                      {exp && <span style={{ color: expired ? 'var(--cw-danger-fg)' : undefined, fontWeight: expired ? 600 : 400 }}> · {expired ? 'Expired' : 'Expires'} {formatDate(exp)}</span>}
                    </div>
                  </div>
                  {clientToggle && !readOnly && (
                    <button type="button" className="cw-btn cw-btn-secondary" aria-pressed={!!f.client_visible} onClick={() => toggleClientVisible(f)}
                      aria-label={`${name}: ${f.client_visible ? 'visible to client' : 'hidden from client'}`}>
                      <i className={`fa-solid ${f.client_visible ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" style={{ color: f.client_visible ? 'var(--cw-success-fg)' : 'var(--cw-text-faint)' }} /> {f.client_visible ? 'Client can see' : 'Hidden from client'}
                    </button>
                  )}
                  <button type="button" className="cw-icon-btn" aria-label={`Download ${name}`} title="Download" onClick={() => doDownload(f)}><i className="fa-solid fa-download" aria-hidden="true" style={{ color: 'var(--cw-blue)' }} /></button>
                  {!readOnly && <button type="button" className="cw-icon-btn" aria-label={`Remove ${name}`} title="Remove" onClick={() => doRemove(f)}><i className="fa-solid fa-trash-can" aria-hidden="true" style={{ color: 'var(--cw-danger-fg)' }} /></button>}
                </li>
              );
            })}
          </ul>
        )}
      {shown.length > 0 && !loadErr && !readOnly && (
        <div aria-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderTop: '1px dashed var(--cw-border)', fontSize: 'var(--cs-text-xs, 0.75rem)', fontWeight: 500, color: 'var(--cs-blue-dark, #1565C0)', background: 'var(--cw-blue-tint, #EBF4FF)' }}>
          <i className="fa-solid fa-cloud-arrow-up" /> {dragOver ? `Drop to attach to this ${entityType}` : 'Drag and drop more files here to attach'}
        </div>
      )}

      {showForm && (
        <Modal
          title={`Attach file — ${title}`}
          maxWidth={500}
          dirty={formProgress == null && !!(form.file || form.title.trim())}
          closeOnBackdrop={formProgress == null}
          hideClose={formProgress != null}
          onClose={() => { if (formProgress == null) setShowForm(false); }}
          footer={({ requestClose }) => (<>
            <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose} disabled={formProgress != null}>Cancel</button>
            <button type="button" className="cw-btn cw-btn-primary" onClick={submitForm} disabled={formProgress != null}><i className={`fa-solid ${formProgress != null ? 'fa-spinner fa-spin' : 'fa-paperclip'}`} aria-hidden="true" /> {formProgress != null ? `Uploading ${formProgress}%` : 'Attach'}</button>
          </>)}
        >
          <Field label="File" required error={formErr.file} hint={form.file ? `${form.file.name} · ${formatBytes(form.file.size)}` : `Up to ${formatBytes(MAX_UPLOAD_BYTES)}`}>
            <button type="button" className="cw-btn cw-btn-secondary" onClick={() => formInputRef.current && formInputRef.current.click()} style={{ width: '100%', justifyContent: 'center', minHeight: 56 }}>
              <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true" /> {form.file ? 'Choose a different file' : 'Choose a file'}
            </button>
          </Field>
          <input ref={formInputRef} type="file" accept={ACCEPTED_FILE_TYPES} tabIndex={-1} aria-hidden="true" style={{ display: 'none' }} onChange={(e) => { pickForForm(e.target.files?.[0]); e.target.value = ''; }} />
          {formProgress != null && (
            <div role="progressbar" aria-label="Uploading" aria-valuemin={0} aria-valuemax={100} aria-valuenow={formProgress} style={{ height: 6, borderRadius: 999, background: 'var(--cw-border-light)', overflow: 'hidden', margin: '-4px 0 12px' }}>
              <div style={{ width: `${formProgress}%`, height: '100%', background: 'var(--cw-blue)' }} />
            </div>
          )}
          <Field label="Title" required error={formErr.title}><input value={form.title} maxLength={200} onChange={(e) => { setForm((f) => ({ ...f, title: e.target.value })); setFormErr((s) => ({ ...s, title: undefined })); }} placeholder="e.g. IPAF licence 2026" /></Field>
          <Field label="Document group">
            <select value={form.document_group} onChange={(e) => setForm((f) => ({ ...f, document_group: e.target.value }))}>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
            <Field label="Issued / go live"><input type="date" value={form.go_live} max={form.expire || undefined} onChange={(e) => { setForm((f) => ({ ...f, go_live: e.target.value })); setFormErr((s) => ({ ...s, expire: undefined })); }} /></Field>
            <Field label="Expires" error={formErr.expire}><input type="date" value={form.expire} min={form.go_live || undefined} onChange={(e) => { setForm((f) => ({ ...f, expire: e.target.value })); setFormErr((s) => ({ ...s, expire: undefined })); }} /></Field>
          </div>
          {files.length > 0 && <p className="cw-meta" style={{ margin: 0 }}>{plural(files.length, 'file')} already attached here.</p>}
        </Modal>
      )}
    </section>
  );
};

export default FileAttachments;
